import { Component, effect, inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { debounceTime, filter, forkJoin, map, Observable, of, pairwise, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { ICanComponentDeactivate } from '@lib-shared/guard/can-deactivate-guard';
import { MessageService } from '@lib-services/message.service';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Order } from '@libs-classes/order.class';
import { IOrder, IOrderBatchUpdateResult, IOrderDataUpdate, IOrderForm } from '@lib-typing/order.interface';
import { OrderStatusEnum, PersonStatusEnum } from '@libs-enums/status.enum';
import { Role } from '@libs-enums/role.enum';
import { CommonModule } from '@angular/common';
import { OrderCustomFieldsComponent } from '@lib-ui/order-custom-fields/order-custom-fields.component';
import { OrdersService } from '@lib-services/orders.service';
import { GroupsSelectorService } from '@lib-services/groups-selector.service';
import { ProjectSelectorService } from '@lib-services/project-selector.service';
import { MatDialog } from '@angular/material/dialog';
import { EnumService } from '@lib-services/enum.service';
import { dateRangeValidator } from '../../../../../../../libs/validators/date-range-validator';
import { OrderContactFormComponent } from '@lib-ui/forms/order-contact-form/order-contact-form.component';
import { IPerson } from '@lib-typing/person.interface';
import { MatFormField, MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MatOption } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { CommonDropdownComponent } from '@lib-ui/common-dropdawn/common-dropdown.component';
import { SignaturePadComponent } from '@lib-ui/signature-pad/signature-pad.component';
import { CommonDateRangeComponent } from '@lib-ui/common-date-range/common-date-range.component';
import { LoaderComponent } from '@lib-ui/loader/loader.component';
import { MatCard, MatCardActions, MatCardContent, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { OrderPriceFormComponent } from '@lib-ui/forms/order-price-form/order-price-form.component';
import { CommonDateRangeErrorsComponent } from '@lib-ui/common-date-range-errors/common-date-range-errors.component';
import { PersonSelectorComponent } from '@lib-ui/person-selector/person-selector.component';
import { AddressEditFormComponent } from '@lib-ui/forms/address-edit-form/address-edit-form.component';
import { LocationSelectorService } from '@lib-services/location-selector.service';
import { ComponentType } from '@angular/cdk/portal';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { SessionStorageService } from '@lib-services/session-storage.service';
import { setDayRange } from '../../../../../../../libs/utils/date-format';
import { TimeZoneService } from '@lib-services/time-zone.service';
import { AssignOrdersService } from '@lib-services/assigns-orders.service';
import {regex} from '@libs-constants/regex';
import { MatTooltip } from '@angular/material/tooltip';
import { amountPaidValidator } from '../../../../../../../libs/validators/amount-paid-validator';

@Component({
    selector: 'app-order-editor',
    standalone: true,
    templateUrl: './order-editor.component.html',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatFormField,
        MatInput,
        MatInputModule,
        MatCheckbox,
        MatButton,
        MatLabel,
        MatDatepickerModule,
        MatNativeDateModule,
        FormsModule,
        MatSelect,
        CommonDropdownComponent,
        SignaturePadComponent,
        CommonDateRangeComponent,
        LoaderComponent,
        MatCard,
        MatCardActions,
        MatCardContent,
        MatCardHeader,
        MatCardTitle,
        MatIcon,
        OrderPriceFormComponent,
        MatIconButton,
        MatOption,
        CommonDateRangeErrorsComponent,
        OrderCustomFieldsComponent,
        PersonSelectorComponent,
        MatTooltip,
    ],
    styleUrl: './order-editor.component.scss'
})
export class OrderEditorComponent implements OnInit, ICanComponentDeactivate, OnDestroy {
    private readonly route = inject(ActivatedRoute);
    private readonly fb = inject(FormBuilder);
    private timeZoneService = inject(TimeZoneService);
    private regex = regex;
    protected locationSelectorService = inject(LocationSelectorService);
    orderId = Number(this.route.snapshot.params['id']);
    order = new Order({} as IOrder, this.timeZoneService);
    totalCostValue = 0;
    orderForm = this.fb.group({
        additionalInfo: '',
        addressDetails: '',
        agreement: '',
        conditionsCheck: false,
        contactId: [0, [Validators.required, Validators.pattern(this.regex.naturalNumber)]],
        dateFrom: [''],
        dateTo: [''],
        description: '',
        executorId: 0,
        groupId: [0, [Validators.required, Validators.pattern(this.regex.naturalNumber)]],
        projectId: [0, [Validators.required, Validators.pattern(this.regex.naturalNumber)]],
        locationId: [0, [Validators.required, Validators.pattern(this.regex.naturalNumber)]],
        discount: false,
        signature: '',
        orderPrices: this.fb.array([
            this.createOrderPriceFormGroup()
        ]),
        prepayment: [0, [Validators.required]],
        amountPaid: [0, [Validators.required]],
        status: [OrderStatusEnum.active, [Validators.required]],
        rejectReason: '',
        rejectDescription: '',
        customFieldsParams: undefined,
    },
    {validators: dateRangeValidator()}); // Подключение валидатора;
    dateRange = '';
    executorAdditionalParams: Params = {
        'filter.status': PersonStatusEnum.active,
        'filter.role': Role.executor,
    };
    fileUrls: SafeUrl[] = [];
    loading = signal<boolean>(false);
    protected readonly OrderStatusEnum = OrderStatusEnum;
    customerAdditionalParams: Params = {
        'filter.role': Role.customer,
    };
    unsubscribe$ = new Subject<void>();

    @ViewChild(OrderCustomFieldsComponent) customFieldsComponent!: OrderCustomFieldsComponent;

    constructor(
        private messageService: MessageService,
        private ordersService: OrdersService,
        private assignOrdersService: AssignOrdersService,
        protected groupsSelectorService: GroupsSelectorService,
        protected projectSelectorService: ProjectSelectorService,
        protected sessionStorageService: SessionStorageService,
        private dialog: MatDialog,
        protected enumService: EnumService,
        protected router: Router,
        private sanitizer: DomSanitizer,
    ) {
        effect(() => {
            if (this.timeZoneService.currentTimeZone() && this.orderId) {
                this.getOrder();
            }
        }, { allowSignalWrites: true });
    }

    ngOnInit(): void {
        // устанавливаем сегодняшнюю дату с рабочим днем +3 часа
        if (!this.dateRange) {
            const start = new Date();
            start.setHours(11, 0, 0, 0);
            const end = new Date();
            end.setHours(21, 0, 0, 0);
            this.dateRange = `${start.toISOString()},${end.toISOString()}`;
        }

        this.orderForm.controls.conditionsCheck.disable();
        this.orderForm.controls.signature.disable();

        if (!this.orderId) {
            this.orderForm.controls.prepayment.removeValidators(Validators.required);
            this.orderForm.controls.amountPaid.removeValidators(Validators.required);
            this.addSubscriptions();
            return;
        }

        this.getOrder();

    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    getOrder(): void {
        this.loading.set(true);
        this.ordersService.getOrder(this.orderId).pipe(filter(order => !!order)).subscribe({
            next: order => {
                this.order.data = order;

                // устанавливаем период текущего времени в выбранной временной зоне
                this.dateRange = `
                    ${this.timeZoneService.setCurrentTimeZoneDateForRange(order.dateFrom).toISOString()},
                    ${this.timeZoneService.setCurrentTimeZoneDateForRange(order.dateTo).toISOString()}
                `;

                if (order.files) {
                    this.fileUrls = order.files.map(file =>
                        this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(this.createBlobFromBase64(file.buffer))));
                }

                if(order.group) {
                    this.executorAdditionalParams = {
                        ...this.executorAdditionalParams,
                        'filter.group.id': order.group.id,
                        'filter.dateFrom': setDayRange(order.dateFrom)
                    };
                }

                // обнуляем массив работ
                this.orderForm.setControl('orderPrices', this.fb.array([this.createOrderPriceFormGroup()]));
                // получаем массив всех контролов
                const pricesArray = this.orderForm.controls.orderPrices;
                if (pricesArray.length === 1 && pricesArray.at(0).value.priceId === 0) {
                    pricesArray.removeAt(0);
                }

                // наполняем массив данными
                order.orderPrices?.forEach(data => {
                    if (data && data.price?.id) {
                        pricesArray.push(
                            this.fb.group({
                                id: [data.id || null],
                                priceId: [data.price.id, Validators.required],
                                itemId: [data.item?.id || null],
                                quantity: [data.quantity || 1, [Validators.min(1), Validators.required]],
                                priceValue: [data.customPrice ?? data.price.price],
                                discountPriceValue: [data.customPrice ?? data.price.discountPrice],
                                customPrice: [data.customPrice ?? null],
                            })
                        );
                    }
                });

                // заполняем форму
                this.orderForm.patchValue(this.order.formData());
                this.addSubscriptions();
            },
            error: () => this.messageService.errorMessage('Задачи не существует'),
        }).add(() => this.loading.set(false));
    }

    addSubscriptions(): void {
        this.orderForm.controls.groupId.valueChanges.pipe(
            startWith(this.orderForm.controls.groupId.value), // Инициализируем поток текущим значением
            pairwise(),
            takeUntil(this.unsubscribe$)
        ).subscribe(([prev, current]) => {
            this.executorAdditionalParams = {
                ...this.executorAdditionalParams,
                'filter.group.id': current,
            };
            if (prev !== current) {
                this.orderForm.controls.executorId.setValue(null);
            }
        });

        this.orderForm.controls.discount.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(value => {
            this.totalCostValue = this.order.totalCost(
                this.orderForm.controls.orderPrices.value, !!value);
            this.amountPaidUpdate(this.totalCostValue);
        });
        this.orderForm.controls.orderPrices.valueChanges.pipe(
            debounceTime(500),
            takeUntil(this.unsubscribe$)
        ).subscribe((value) => {
            this.totalCostValue = this.order.totalCost(
                value, !!this.orderForm.controls.discount.value);
            this.amountPaidUpdate(this.totalCostValue);
        });
    }

    amountPaidUpdate(total: number): void {
        const amountPaidControl = this.orderForm.controls.amountPaid;
        const prepayment = this.order.data.prepayment ?? 0;
        const newAmountPaid = Number((total - prepayment).toFixed(2));
        amountPaidControl.setValue(newAmountPaid, { emitEvent: false });

        amountPaidControl.clearValidators();
        amountPaidControl.setValidators([
            Validators.required,
            amountPaidValidator(total, prepayment)
        ]);
        amountPaidControl.updateValueAndValidity({ emitEvent: false });
    }

    setDate(): void {
        const from = this.orderForm.get('dateFrom')?.value;
        if (from) {
            const newFrom = this.timeZoneService.setCurrentTimeZoneDateSave(new Date(from));
            this.orderForm.controls.dateFrom.setValue(newFrom.toISOString());
        }

        const to = this.orderForm.get('dateTo')?.value;
        if (to) {
            const newTo = this.timeZoneService.setCurrentTimeZoneDateSave(new Date(to));
            this.orderForm.controls.dateTo.setValue(newTo.toISOString());
        }
    }

    onSubmit(redirect?: boolean): void {

        if (!this.orderForm.valid) {
            this.messageService.infoMessage('Заполнены не все данные');
            return;
        }

        this.setDate();

        this.loading.set(true);

        // Получение кастомных полей
        if (this.customFieldsComponent?.customFields?.length) {
            const customFieldsValue = this.customFieldsComponent.getCustomFieldsValue();
            this.orderForm.controls.customFieldsParams.setValue(customFieldsValue);
        }

        const request$ = this.order.data.id
            ? this.ordersService.updateOrder(this.order.dataForUpdate(this.orderForm.value as IOrderForm))
            : this.ordersService.createOrder(this.order.dataForCreate(this.orderForm.value as IOrderForm));

        request$.pipe(
            switchMap(order => {
                const oldExecutorId = this.order.data.executor?.id;
                const newExecutorId = this.orderForm.controls.executorId.value;

                // Если исполнитель не менялся — просто возвращаем order
                if (oldExecutorId === newExecutorId) {
                    return of(order);
                }

                const requests: Observable<IOrderBatchUpdateResult>[] = [];

                // Если БЫЛ старый исполнитель — обновляем его очередь
                if (oldExecutorId) {
                    requests.push(
                        this.getExecutorOrders(oldExecutorId)
                    );
                }

                // Если ЕСТЬ новый исполнитель — обновляем его очередь
                if (newExecutorId) {
                    requests.push(
                        this.getExecutorOrders(newExecutorId)
                    );
                }

                // Если есть запросы — выполняем forkJoin, иначе просто order
                return requests.length ? forkJoin(requests).pipe(map(() => order)) : of(order);
            })
        ).subscribe({
            next: order => {
                this.order.data = order;
                this.orderForm.patchValue(this.order.formData());
                if (redirect) {
                    this.orderForm.markAsPristine();
                    this.router.navigate(['order']);
                }
                this.messageService.successMessage('Заявка успешно сохранена');
            },
            error: () => this.messageService.errorMessage('Ошибка при сохранении'),

        }).add(() => this.loading.set(false));
    }

    getExecutorOrders(executorId: number): Observable<IOrderBatchUpdateResult> {
        const query: Params = {
            'filter.dateFrom': setDayRange(this.order.data.dateFrom),
            'filter.executor': executorId,
        };

        return this.ordersService.getAllOrders(query).pipe(
            switchMap(result => {
                const updates = this.assignOrdersService.setQueueAfterMultiEdit(result).map(order => ({
                    id: order.data.id,
                    status: order.data.status === OrderStatusEnum.active
                        ? OrderStatusEnum.destributed
                        : order.data.status,
                    queueNumber: order.data.queueNumber,
                } as IOrderDataUpdate));
                return this.assignOrdersService.saveOrders(updates);
            })
        );
    }

    // получает данные из common date range и устанавливает значения dateFrom dateTo
    changeDate(date: string | null): void {
        const [dateFrom, dateTo] = date ? date.split(',').map(this.toISOString) : [null, null];
        this.orderForm.patchValue({dateFrom, dateTo});
    }

    // устанавливает текущее время относительно Гринвича
    toISOString(date: string): string {
        const originalDate = new Date(date);
        return originalDate.toISOString();
    }

    // добавляем новый контакт при создании заявки
    editContact(): void {
        this.openDialog(
            OrderContactFormComponent,
            this.orderForm.controls.contactId.value
        );
    }

    openDialog(component: ComponentType<unknown>, id: number | null): Observable<IPerson> {
        return this.dialog.open(component, {
            width: '50%',
            height: 'auto',
            data: {
                personId: id,
            }
        }).afterClosed().pipe(filter(data => data));
    }

    addNewLocation():void {
        this.dialog.open(AddressEditFormComponent, {
            width: '50%',
            disableClose: true,
            data: {},
        });
    }

    addOrderPrice(): void {
        // если заявки еще не существует в БД - создаем
        if (!this.order.data.id) {
            this.onSubmit();
        }

        this.orderForm.controls.orderPrices.push(this.createOrderPriceFormGroup());
    }

    // удаляем объект из массива orderPrices
    removeOrderPrice(index: number): void {
        this.messageService.confirmMessage({
            title: 'Вы действительно хотите удалить работу?',
            primaryText: 'Подтвердить',
            secondaryText: 'Отменить',
            showSecondaryButton: true,
        }).pipe(
            filter(confirm => confirm)
        ).subscribe(() => {
            this.orderForm.controls.orderPrices.removeAt(index);
        });
    }

    deleteOrder(id: number): void {
        if (!this.order.data) {return;}

        this.messageService.confirmMessage({
            title: 'Удаление заявки',
            text: 'Вы действительно хотите удалить заявку?',
            primaryText: 'Удалить',
            secondaryText: 'Отмена',
            showSecondaryButton: true,
        }).pipe(
            filter(result => result),
            switchMap(() => {
                return this.ordersService.deleteOrders([id]);
            }),
        ).subscribe({
            next: () => this.messageService.successMessage('Заявка успешно удалена'),
            error: () => this.messageService.errorMessage('При удалении произошла ошибка')
        });
    }

    canDeactivate(): Observable<boolean> | boolean {
        if (this.orderForm.dirty) {
            return this.messageService.confirmMessage({
                title: 'У вас есть несохраненные изменения!',
                text: 'Вы действительно хотите закрыть окно?',
                primaryText: 'Да',
                secondaryText: 'Отмена'
            });
        }
        return true;
    }

    private createOrderPriceFormGroup(): FormGroup {
        return this.fb.group({
            priceId: [0, Validators.required],
            itemId: [0],
            quantity: [1, [Validators.min(1), Validators.required]],
            priceValue: [0, [Validators.min(0)]],
            discountPriceValue: [0, [Validators.min(0)]],
            customPrice: [null, [Validators.min(0)]],
        });
    }

    createBlobFromBase64(b64Data: string, contentType = ''): Blob {
        const byteCharacters = atob(b64Data);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: contentType });
    }

    orderLogs(): void {
        this.sessionStorageService.setItem('logs-filters', {entityId: this.order.data.id});
        this.router.navigate(['logs'], {
            queryParams: { showBackBtn: true} // передаем флаг для возращения от таблицы к заявке
        });
    }
}
